import { useState, useCallback } from 'react';
import firebaseService from '../utils/services/FirebaseService';

/**
 * React hook for Firebase email functionality
 * Provides easy access to email functions with loading states and error handling
 */
export const useFirebaseEmail = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Send admin email
   */
  const sendAdminEmail = useCallback(async (subject, text, html, attachments = []) => {
    setLoading(true);
    setError(null);

    try {
      const result = await firebaseService.sendAdminEmail(subject, text, html, attachments);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to send admin email';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Send email to specific user
   */
  const sendUserEmail = useCallback(async (uid, subject, text, html, attachments = []) => {
    setLoading(true);
    setError(null);

    try {
      const result = await firebaseService.sendUserEmail(uid, subject, text, html, attachments);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to send user email';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Send email to multiple users by admin
   */
  const sendEmailByAdmin = useCallback(async (uids, subject, text, html, attachments = []) => {
    setLoading(true);
    setError(null);

    try {
      const result = await firebaseService.sendEmailByAdmin(uids, subject, text, html, attachments);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to send emails to users';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Send password reset email
   */
  const sendPasswordResetEmail = useCallback(async (email) => {
    setLoading(true);
    setError(null);

    try {
      const result = await firebaseService.sendPasswordResetEmail(email);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to send password reset email';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Send email verification to current user
   */
  const sendEmailVerification = useCallback(async (url = null) => {
    setLoading(true);
    setError(null);

    try {
      const result = await firebaseService.sendEmailVerification(url);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to send email verification';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // State
    loading,
    error,
    
    // Actions
    sendAdminEmail,
    sendUserEmail,
    sendEmailByAdmin,
    sendPasswordResetEmail,
    sendEmailVerification,
    clearError
  };
};

export default useFirebaseEmail; 